import type { EmailSortDirection } from '@packages/types';

// Pure helpers for grouping a list of items into per-day buckets keyed by the
// user's local timezone. Used by the emails and calendar list pages so both
// surfaces present the same day-grouped row shape.
//
// "Day key" is YYYY-MM-DD in the user's local timezone. Grouping by local day
// matches user expectations - an 18:00 SA meeting falls under that SA day, not
// the UTC day. Header labels use TODAY / YESTERDAY / TOMORROW for the days
// adjacent to now, and weekday + date for everything else (e.g. WED 24 APR).

/** YYYY-MM-DD for a given Date in the user's local timezone. */
export function getLocalDayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** YYYY-MM-DD for `now` in the user's local timezone. */
export function todayLocalDayKey(now: Date = new Date()): string {
  return getLocalDayKey(now);
}

/**
 * Add `days` calendar days to a YYYY-MM-DD string, returning a new YYYY-MM-DD.
 * Operates on a local-noon Date so DST transitions don't cause off-by-one days.
 */
export function addDaysLocal(dayKey: string, days: number): string {
  const [y, m, d] = dayKey.split('-').map(Number);
  const date = new Date(y, m - 1, d, 12, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return getLocalDayKey(date);
}

/**
 * Header label for a day group: TODAY / YESTERDAY / TOMORROW or weekday + date,
 * always with the calendar date (e.g. `WED 24 APR` or `TODAY · 27 APR`).
 */
export function formatDayHeader(dayKey: string, todayKey: string = todayLocalDayKey()): string {
  const [y, m, d] = dayKey.split('-').map(Number);
  const date = new Date(y, m - 1, d, 12, 0, 0, 0);
  const datePart = date
    .toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' })
    .toUpperCase();

  const yesterdayKey = addDaysLocal(todayKey, -1);
  const tomorrowKey = addDaysLocal(todayKey, 1);

  if (dayKey === todayKey) return `TODAY · ${datePart}`;
  if (dayKey === yesterdayKey) return `YESTERDAY · ${datePart}`;
  if (dayKey === tomorrowKey) return `TOMORROW · ${datePart}`;

  const weekday = date
    .toLocaleDateString('en-ZA', { weekday: 'short' })
    .toUpperCase()
    .replace('.', '');
  return `${weekday} ${datePart}`;
}

export interface DayGroup<T> {
  dayKey: string;
  label: string;
  items: T[];
}

/**
 * Group a list of items by their date (in the user's local timezone). Days are
 * returned newest first (descending by dayKey). `withinDayDirection` controls
 * the sort order within a day: 'asc' reads chronologically down the day (good
 * for calendar events), 'desc' reads newest-first (good for emails).
 *
 * `getDate(item)` returns the item's primary timestamp - whatever the caller
 * wants to bucket on (an event's `startAt`, an email's `date`, etc.). It must
 * return a Date or an ISO 8601 string parsable by `new Date()`.
 */
export function groupItemsByDay<T>(
  items: T[],
  getDate: (item: T) => Date | string,
  options: { withinDayDirection?: EmailSortDirection; now?: Date } = {},
): DayGroup<T>[] {
  const now = options.now ?? new Date();
  const todayKey = getLocalDayKey(now);
  const direction = options.withinDayDirection ?? 'asc';
  const groups = new Map<string, Array<{ date: Date; item: T }>>();

  for (const item of items) {
    const raw = getDate(item);
    const date = raw instanceof Date ? raw : new Date(raw);
    const dayKey = getLocalDayKey(date);
    const list = groups.get(dayKey);
    if (list) {
      list.push({ date, item });
    } else {
      groups.set(dayKey, [{ date, item }]);
    }
  }

  return Array.from(groups.entries())
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .map(([dayKey, entries]) => {
      const sorted = entries.slice().sort((a, b) =>
        direction === 'asc'
          ? a.date.getTime() - b.date.getTime()
          : b.date.getTime() - a.date.getTime(),
      );
      return {
        dayKey,
        label: formatDayHeader(dayKey, todayKey),
        items: sorted.map((entry) => entry.item),
      };
    });
}
